import BulletList from "@tiptap/extension-bullet-list";
import Document from "@tiptap/extension-document";
import History from "@tiptap/extension-history";
import Link from "@tiptap/extension-link";
import ListItem from "@tiptap/extension-list-item";
import OrderedList from "@tiptap/extension-ordered-list";
import Paragraph from "@tiptap/extension-paragraph";
import Placeholder from "@tiptap/extension-placeholder";
import Text from "@tiptap/extension-text";
import { EditorContent, useEditor } from "@tiptap/react";
import * as React from "react";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import Marker, { IMarker } from "./markers.plugin";
import StopWords from "./stopWords.plugin";

export * from "./markers.plugin";

export interface IRichEditorRef {
  getContentElement: () => HTMLDivElement;
  createMarker: (selection: ISelection, marker: IMarker) => void;
  removeMarker: (markerId: string) => void;
  focus: () => void;
}

export enum RichEditorMode {
  Json = "json",
  Text = "text",
}

export interface ISelection {
  from: number;
  to: number;
}

interface IRichEditorProps {
  value: string;
  placeholder?: string;
  className?: string;
  focusClassName?: string;
  wrapperClassName?: string;
  disabled?: boolean;
  stopWordsList?: { word: string; caption: string }[];
  onChange: (value: string) => void;
  mode?: RichEditorMode;
  onFocus?: () => void;
  onBlur?: () => void;
  markers?: IMarker[];
  onTextSelectionUpdate?: (selection: ISelection) => void;
  onMarkerSelected?: (markerId: string) => void;
}

const TEXT_SELECTION_UPDATE_DEBOUNCE = 400;

const RichEditor = forwardRef<IRichEditorRef, IRichEditorProps>(
  (props, ref) => {
    const {
      value,
      placeholder,
      className,
      markers,
      wrapperClassName,
      disabled,
      stopWordsList,
      mode,
      onChange,
      onFocus,
      onBlur,
      onTextSelectionUpdate,
      onMarkerSelected,
    } = props;

    const editorRef = useRef(null);
    const editorTextSelectionTimerRef = useRef<number>();

    const editor = useEditor(
      {
        extensions: [
          History,
          Document,
          Paragraph,
          Text,
          ListItem,
          BulletList,
          OrderedList,
          Link,
          Placeholder.configure({
            placeholder,
            emptyEditorClass: "is-editor-empty text-black-400",
          }),
          Marker.configure({ onMarkerSelected }),
          StopWords.configure({
            stopWordsList,
          }),
        ],
        editorProps: {
          attributes: {
            class: className || "",
          },
        },
        editable: !disabled,
        content: (() => {
          if (!value || typeof value !== "string") {
            return value;
          }
          // Проверяем, что строка действительно похожа на JSON (начинается с { и заканчивается })
          const trimmedValue = value.trim();
          if (
            trimmedValue.indexOf("{") === 0 &&
            trimmedValue.lastIndexOf("}") === trimmedValue.length - 1
          ) {
            try {
              const parsed = JSON.parse(trimmedValue);
              return parsed;
            } catch (error) {
              // Если не удалось распарсить как JSON, возвращаем как обычный текст
              console.warn(
                "Failed to parse content as JSON, using as plain text:",
                error
              );
              return value;
            }
          }
          return value;
        })(),
      },
      []
    );

    useEffect(() => {
      if (editor) {
        editor.setOptions({ editable: !disabled });
      }
    }, [editor, disabled]);

    useEffect(() => {
      if (editor) {
        const { from, to } = editor.state.selection;

        const parsedContent = (() => {
          if (!value || typeof value !== "string") {
            return value;
          }
          // Проверяем, что строка действительно похожа на JSON (начинается с { и заканчивается })
          const trimmedValue = value.trim();
          if (
            trimmedValue.indexOf("{") === 0 &&
            trimmedValue.lastIndexOf("}") === trimmedValue.length - 1
          ) {
            try {
              const parsed = JSON.parse(trimmedValue);
              return parsed;
            } catch (error) {
              console.warn(
                "Failed to parse content as JSON, using as plain text:",
                error
              );
              return value;
            }
          }
          return value;
        })();

        editor.commands.setContent(parsedContent, false, {
          preserveWhitespace: "full",
        });
        editor.commands.setTextSelection({ from, to });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor, value]);

    useEffect(() => {
      if (editor && markers) {
        editor.commands.updateAllMarkers(markers);
      }
    }, [editor, markers]);

    useEffect(() => {
      if (editor) {
        editor.on("update", ({ editor }) => {
          if (editor.isEmpty) {
            onChange("");
          } else {
            if (mode === RichEditorMode.Text) {
              onChange(editor.getText());
            } else {
              onChange(JSON.stringify(editor.getJSON()));
            }
          }
        });
      }

      return () => {
        if (editor) {
          editor.off("update");
        }
      };
    }, [editor, mode, onChange]);

    useEffect(() => {
      if (editor && onBlur) {
        editor.on("blur", onBlur);
      }

      return () => {
        if (editor) {
          editor.off("blur");
        }
      };
    }, [editor, onBlur]);

    useEffect(() => {
      if (editor && onFocus) {
        editor.on("focus", onFocus);
      }

      return () => {
        if (editor) {
          editor.off("focus");
        }
      };
    }, [editor, onFocus]);

    useEffect(() => {
      if (editor && (onMarkerSelected || onTextSelectionUpdate)) {
        editor.on("selectionUpdate", ({ editor }) => {
          if (onTextSelectionUpdate) {
            const { from, to } = editor.state.selection;

            window.clearTimeout(editorTextSelectionTimerRef.current);
            editorTextSelectionTimerRef.current = window.setTimeout(() => {
              console.log("update");
              onTextSelectionUpdate({ from, to });
            }, TEXT_SELECTION_UPDATE_DEBOUNCE);
          }
        });
      }

      return () => {
        if (editor) {
          editor.off("selectionUpdate");
        }
      };
    }, [editor, onMarkerSelected, onTextSelectionUpdate, markers]);

    useImperativeHandle(
      ref,
      () => ({
        getContentElement: () => {
          return (editorRef.current as any)?.editorContentRef.current;
        },

        createMarker(selection: ISelection, marker: IMarker) {
          editor?.commands.setMarker(selection, marker);
        },

        removeMarker(id: string) {
          editor?.commands.removeMarker(id);
        },

        focus() {
          editor?.commands.focus();
        },
      }),
      [editor]
    );

    return (
      <EditorContent
        ref={editorRef}
        className={wrapperClassName}
        editor={editor}
      />
    );
  }
);

export default RichEditor;
